import { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../../adapters/api-client';
import { Country } from '../../../adapters/api-client/referential/Referential.model';
import { ComboInput, ComboInputDS } from '../../../components/DesignSystem';
import { useFetch } from '../../../hooks/useFetch';
import { NationalityInput } from '../../../store/constants';
import { ComboModalErrorContainer, ComboModalErrorMessage } from '../../../styles/ModalStyle';
import { Title1 } from '../../../styles/TypoStyle';
import { ValidityHtmlElement } from '../../../utils/DsType';
import { removeFranceFromCountries, sortCountries } from '../../../utils/LocalityUtils';
import binocular from '../assets/binocular.svg';

interface NationalityModalProps {
  input: string;
  scope: string;
  comboListHeight?: string;
}
export const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: baseline;
  margin-top: 16px;
  overflow: auto;

  combo-input {
    color: var(--colors-on-background);
    font-family: var(--typo-form-font-family);
    font-size: var(--typo-form-font-size);
    letter-spacing: var(--typo-form-letter-spacing);
    line-height: var(--typo-form-line-height);
    font-weight: var(--typo-form-font-weight);
    align-self: stretch;
    --item-height: initial;
    --item-margin-top-bottom: 11.5px;
    --list-height: ${(props: { listHeight: string }) => props.listHeight};
  }
`;

const ModalTitle = styled(Title1)``;

const NationalityModal: FunctionComponent<NationalityModalProps> = ({
  input,
  scope,
  comboListHeight,
}): ReactElement => {
  const inputRef = useRef(null);
  const [showError, updateHiddenError] = useState(false);
  const { response: countriesFromApi } = useFetch(() => apiClient().referential.getCountries());
  const [countriesState, setCountriesState] = useState<Country[]>([]);

  const retrieveCountries = () => {
    if (countriesFromApi) {
      const countriesWithoutFrance = removeFranceFromCountries(countriesFromApi);
      setCountriesState(sortCountries(countriesWithoutFrance));
      if (!!inputRef && inputRef.current !== null) {
        if (countriesWithoutFrance) (inputRef.current as unknown as ValidityHtmlElement).hasFocus = true;
      }
    }
  };

  const onSearch = () => {
    if (inputRef.current && inputRef.current !== null) {
      updateHiddenError(
        (inputRef.current as unknown as ValidityHtmlElement).hasFocus &&
          (inputRef.current as unknown as ComboInputDS).filteredItems?.length === 0 &&
          countriesState.length !== 0,
      );
    }
  };

  const onBlurEvent = (event: Event) => {
    if (inputRef.current && inputRef.current !== null) {
      if (event && event.target && (event.target as unknown as ComboInputDS).value === '') {
        (inputRef.current as unknown as ValidityHtmlElement).valid = false;
      }
    }
  };

  useEffect(() => {
    retrieveCountries();
  }, [countriesFromApi]);

  return (
    <Container {...{ listHeight: comboListHeight || 'initial' }}>
      <ModalTitle>
        Choisissez votre {scope === NationalityInput.NATIONALITY ? 'nationalité' : 'pays de naissance'}
      </ModalTitle>
      <ComboInput
        list-on-open
        ref={inputRef}
        class="trackable-list-click-in-modal"
        field="label"
        items={JSON.stringify(countriesState)}
        pattern=".*"
        align-items="left"
        placeholder={
          scope === NationalityInput.NATIONALITY ? 'Recherchez votre nationalité' : 'Recherchez votre pays de naissance'
        }
        onItemsChanged={onSearch}
        onListBlur={onBlurEvent}
        value={input}
        id={scope === NationalityInput.NATIONALITY ? 'Nationalité' : 'Pays de naissance'}
      ></ComboInput>
      <ComboModalErrorContainer id="errorContainer" {...{ showError }}>
        <img src={binocular} alt="" />
        <ComboModalErrorMessage>
          Nous n’avons trouvé aucun résultat correspondant à votre recherche.
        </ComboModalErrorMessage>
      </ComboModalErrorContainer>
    </Container>
  );
};

export default NationalityModal;
